import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  AcademicCapIcon,
  MagnifyingGlassIcon,
  ClockIcon,
  UserGroupIcon,
  StarIcon,
  FunnelIcon,
  BookOpenIcon,
  CheckBadgeIcon,
} from '@heroicons/react/24/outline'
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid'
import { Card, Badge, Avatar, InlineSpinner, EmptyState, Pagination } from '@/components/ui'
import { lmsApi } from '@/api'
import type { Course, CourseCategory } from '@/api/lms'

export default function CoursesPage() {
  const [courses, setCourses] = useState<Course[]>([])
  const [categories, setCategories] = useState<CourseCategory[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const [selectedLevel, setSelectedLevel] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [showFilters, setShowFilters] = useState(false)

  useEffect(() => {
    fetchCategories()
  }, [])

  useEffect(() => {
    fetchCourses()
  }, [currentPage, searchQuery, selectedCategory, selectedLevel])

  const fetchCategories = async () => {
    try {
      const data = await lmsApi.getCategories()
      setCategories(data)
    } catch (error) {
      console.error('Error fetching categories:', error)
    }
  }

  const fetchCourses = async () => {
    setLoading(true)
    try {
      const data = await lmsApi.getCourses({
        page: currentPage,
        search: searchQuery || undefined,
        category: selectedCategory || undefined,
        level: selectedLevel || undefined,
        ordering: '-created_at',
      })
      setCourses(data.results)
      setTotalPages(Math.ceil(data.count / 12))
      setTotalItems(data.count)
    } catch (error) {
      console.error('Error fetching courses:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setCurrentPage(1)
    fetchCourses()
  }

  const getLevelBadge = (level: Course['level']) => {
    const config: Record<string, { variant: 'success' | 'warning' | 'danger'; label: string }> = {
      basic: { variant: 'success', label: 'Base' },
      beginner: { variant: 'success', label: 'Base' },
      intermediate: { variant: 'warning', label: 'Intermedio' },
      advanced: { variant: 'danger', label: 'Avanzato' },
    }
    return config[level] || { variant: 'info' as const, label: level || 'N/D' }
  }

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center">
        {[1, 2, 3, 4, 5].map((star) => (
          star <= Math.round(rating) ? (
            <StarSolidIcon key={star} className="h-4 w-4 text-yellow-400" />
          ) : (
            <StarIcon key={star} className="h-4 w-4 text-gray-300" />
          )
        ))}
        <span className="ml-1 text-sm text-gray-600">({rating.toFixed(1)})</span>
      </div>
    )
  }

  if (loading && currentPage === 1) {
    return <InlineSpinner message="Caricamento corsi..." />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Corsi</h1>
          <p className="text-gray-600">Esplora il catalogo corsi e migliora le tue competenze</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to="/my-certificates"
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 bg-white rounded-lg hover:bg-gray-50 transition-colors"
          >
            <CheckBadgeIcon className="h-5 w-5 mr-2" />
            I miei attestati
          </Link>
          <Link
            to="/courses/my-courses"
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <BookOpenIcon className="h-5 w-5 mr-2" />
            I miei corsi
          </Link>
        </div>
      </div>

      {/* Search and Filters */}
      <Card>
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Cerca corsi..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                showFilters ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-700'
              }`}
            >
              <FunnelIcon className="h-5 w-5" />
              Filtri
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Cerca
            </button>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Categoria
                </label>
                <select
                  value={selectedCategory}
                  onChange={(e) => {
                    setSelectedCategory(e.target.value)
                    setCurrentPage(1)
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Tutte le categorie</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.slug}>
                      {cat.name} ({cat.courses_count})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Livello
                </label>
                <select
                  value={selectedLevel}
                  onChange={(e) => {
                    setSelectedLevel(e.target.value)
                    setCurrentPage(1)
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Tutti i livelli</option>
                  <option value="beginner">Base</option>
                  <option value="intermediate">Intermedio</option>
                  <option value="advanced">Avanzato</option>
                </select>
              </div>
            </div>
          )}
        </form>
      </Card>

      {/* Categories Quick Filter */}
      {categories.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          <button
            onClick={() => {
              setSelectedCategory('')
              setCurrentPage(1)
            }}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
              !selectedCategory
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Tutti
          </button>
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => {
                setSelectedCategory(cat.slug)
                setCurrentPage(1)
              }}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                selectedCategory === cat.slug
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {cat.icon && <span className="mr-1">{cat.icon}</span>}
              {cat.name}
            </button>
          ))}
        </div>
      )}

      {/* Courses Grid */}
      {courses.length > 0 ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((course) => (
              <Link
                key={course.id}
                to={`/courses/${course.slug}`}
                className="group"
              >
                <Card className="h-full hover:shadow-lg transition-shadow">
                  {/* Thumbnail */}
                  <div className="relative h-40 -mx-4 -mt-4 mb-4 overflow-hidden rounded-t-lg bg-gray-100">
                    {(course.thumbnail_url || course.thumbnail) ? (
                      <img
                        src={course.thumbnail_url || course.thumbnail || ''}
                        alt={course.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary-100 to-primary-200">
                        <AcademicCapIcon className="h-16 w-16 text-primary-400" />
                      </div>
                    )}
                    {course.is_enrolled && (
                      <div className="absolute top-2 left-2">
                        <Badge variant="success">Iscritto</Badge>
                      </div>
                    )}
                    <div className="absolute top-2 right-2">
                      <Badge variant={getLevelBadge(course.level).variant}>
                        {getLevelBadge(course.level).label}
                      </Badge>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="space-y-3">
                    {course.category && (
                      <Badge size="sm">{course.category.name}</Badge>
                    )}
                    <h3 className="font-semibold text-gray-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {course.title}
                    </h3>
                    <p className="text-sm text-gray-500 line-clamp-2">
                      {course.short_description || course.description}
                    </p>

                    {/* Instructor */}
                    {course.instructor && (
                      <div className="flex items-center gap-2">
                        <Avatar
                          size="sm"
                          name={course.instructor.full_name}
                          src={course.instructor.avatar || undefined}
                        />
                        <span className="text-sm text-gray-600">
                          {course.instructor.full_name}
                        </span>
                      </div>
                    )}

                    {/* Stats */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center">
                          <ClockIcon className="h-4 w-4 mr-1" />
                          {course.duration_hours}h
                        </span>
                        <span className="flex items-center">
                          <BookOpenIcon className="h-4 w-4 mr-1" />
                          {course.lessons_count} lezioni
                        </span>
                      </div>
                      {course.rating && course.rating > 0 && renderStars(course.rating)}
                    </div>

                    {/* Progress bar for enrolled courses */}
                    {course.is_enrolled && course.progress !== undefined && (
                      <div className="pt-2">
                        <div className="flex justify-between text-xs text-gray-500 mb-1">
                          <span>Progresso</span>
                          <span>{course.progress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-primary-600 h-2 rounded-full transition-all"
                            style={{ width: `${course.progress}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Enrolled count */}
                    <div className="flex items-center text-xs text-gray-400">
                      <UserGroupIcon className="h-4 w-4 mr-1" />
                      {course.enrolled_count} iscritti
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={12}
            onPageChange={setCurrentPage}
          />
        </>
      ) : (
        <EmptyState
          icon={<AcademicCapIcon className="h-12 w-12" />}
          title={searchQuery || selectedCategory || selectedLevel ? 'Nessun corso trovato' : 'Nessun corso disponibile'}
          description={
            searchQuery || selectedCategory || selectedLevel
              ? 'Prova a modificare i filtri di ricerca'
              : 'I corsi saranno disponibili a breve'
          }
        />
      )}
    </div>
  )
}
